import { useEffect, useState } from "react"
import { Link } from "react-router-dom"
import { get } from "../../api/api"

function ProviderCard({ name, data = {}, fields = [] }) {
  const ready = fields.every((field) => data[field.key])

  return (
    <div className="card">
      <div className="score-bar-top">
        <div>
          <p className="eyebrow">Provider</p>
          <h2>{name}</h2>
        </div>
        <span className={`status ${ready ? "active" : "warning"}`}>{ready ? "Ready" : "Setup needed"}</span>
      </div>
      <div className="control-list">
        {fields.map((field) => (
          <div className="control-item" key={field.key}>
            <div>
              <strong>{field.label}</strong>
              <p className="muted">{field.helper}</p>
            </div>
            <span className={`status ${data[field.key] ? "active" : "inactive"}`}>
              {data[field.key] ? "Set" : "Missing"}
            </span>
          </div>
        ))}
      </div>
    </div>
  )
}

export default function Billing() {
  const [billing, setBilling] = useState(null)
  const [error, setError] = useState("")

  useEffect(() => {
    get("/admin/billing")
      .then((res) => setBilling(res.data))
      .catch((err) => setError(err.response?.data?.error || "Failed to load billing"))
  }, [])

  const stripe = billing?.providers?.stripe || {}
  const razorpay = billing?.providers?.razorpay || {}

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h1>Billing Providers</h1>
          <p className="muted">Control payment readiness, active provider, trial policy and plan monetization.</p>
        </div>
        <Link className="button" to="/admin/settings">Configure keys</Link>
      </div>

      {error && <p className="error">{error}</p>}

      <div className="grid">
        <div className="metric-card">
          <p className="metric-label">Active provider</p>
          <div className="metric">{billing?.provider || "manual"}</div>
          <p className="muted">Detected from saved provider keys</p>
        </div>
        <div className="metric-card">
          <p className="metric-label">Currency</p>
          <div className="metric">{billing?.currency || "USD"}</div>
          <p className="muted">Default plan currency</p>
        </div>
        <div className="metric-card">
          <p className="metric-label">Plans</p>
          <div className="metric">{billing?.plans || 0}</div>
          <p className="muted">Packages available for sale</p>
        </div>
        <div className="metric-card">
          <p className="metric-label">Trial</p>
          <div className="metric">{billing?.trialDays || 0}d</div>
          <p className="muted">{billing?.graceDays || 0} grace days after failed payment</p>
        </div>
      </div>

      <div className="split-grid pro-section">
        <ProviderCard
          name="Stripe"
          data={stripe}
          fields={[
            { key: "publicKey", label: "Public key", helper: "Used by checkout UI" },
            { key: "secretKey", label: "Secret key", helper: "Used by server billing actions" },
            { key: "webhookSecret", label: "Webhook secret", helper: "Verifies Stripe event delivery" }
          ]}
        />
        <ProviderCard
          name="Razorpay"
          data={razorpay}
          fields={[
            { key: "keyId", label: "Key ID", helper: "Used by checkout UI" },
            { key: "keySecret", label: "Key secret", helper: "Used by server billing actions" },
            { key: "webhookSecret", label: "Webhook secret", helper: "Verifies Razorpay event delivery" }
          ]}
        />
      </div>

      <div className="card pro-section">
        <h2>Billing Policy</h2>
        <div className="control-list">
          <div className="control-item">
            <div>
              <strong>Require payment for new users</strong>
              <p className="muted">When enabled, users need an active plan after trial.</p>
            </div>
            <span className={`status ${billing?.requirePayment ? "active" : "inactive"}`}>
              {billing?.requirePayment ? "Enabled" : "Disabled"}
            </span>
          </div>
          <div className="control-item">
            <div>
              <strong>Manual fallback</strong>
              <p className="muted">If no provider is ready, plans can still be managed manually.</p>
            </div>
            <span className="status good">Available</span>
          </div>
        </div>
      </div>
    </div>
  )
}
